import { create } from 'zustand';
import type { SceneSource, Resolution, AdminToggles } from '../types/obs';

interface AppState {
  selectedSceneId: string | null;
  setSelectedSceneId: (id: string) => void;

  selectedCategoryId: string | null;
  setSelectedCategoryId: (id: string) => void;

  searchQuery: string;
  setSearchQuery: (query: string) => void;

  queue: SceneSource[];
  addToQueue: (source: SceneSource) => void;
  removeFromQueue: (sourceId: string) => void;
  clearQueue: () => void;

  resolution: Resolution;
  setResolution: (res: Resolution) => void;

  adminToggles: AdminToggles;
  loadAdminToggles: () => void;
  setAdminToggle: (templateId: string, enabled: boolean) => void;
  saveAdminToggles: () => void;

  isVertical: boolean;
  setIsVertical: (vertical: boolean) => void;
}

const ADMIN_KEY = 'scenr_admin_toggles';

// Load toggles from localStorage on initialization
function getInitialToggles(): AdminToggles {
  try {
    const saved = localStorage.getItem(ADMIN_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.error('Failed to load admin toggles', e);
  }
  return {};
}

export const useAppStore = create<AppState>((set, get) => ({
  selectedSceneId: null,
  setSelectedSceneId: (id) => set({ selectedSceneId: id }),

  selectedCategoryId: 'all',
  setSelectedCategoryId: (id) => set({ selectedCategoryId: id }),

  searchQuery: '',
  setSearchQuery: (query) => set({ searchQuery: query }),

  queue: [],
  addToQueue: (source) => set((state) => ({ queue: [...state.queue, source] })),
  removeFromQueue: (sourceId) => set((state) => ({ queue: state.queue.filter(s => s.id !== sourceId) })),
  clearQueue: () => set({ queue: [] }),

  resolution: { id: '1080p', name: '1080p', w: 1920, h: 1080, label: '1920×1080' },
  setResolution: (res) => set({ resolution: res }),

  // Initialize from localStorage immediately
  adminToggles: getInitialToggles(),
  loadAdminToggles: () => {
    try {
      const saved = localStorage.getItem(ADMIN_KEY);
      if (saved) {
        set({ adminToggles: JSON.parse(saved) });
      }
    } catch (e) {
      console.error('Failed to load admin toggles', e);
    }
  },
  setAdminToggle: (templateId, enabled) => set((state) => ({
    adminToggles: { ...state.adminToggles, [templateId]: enabled }
  })),
  saveAdminToggles: () => {
    const toggles = get().adminToggles;
    localStorage.setItem(ADMIN_KEY, JSON.stringify(toggles));
  },

  isVertical: false,
  setIsVertical: (vertical) => set({ isVertical: vertical }),
}));
